/**
 * Solver / Apalancamiento Panel
 * ─────────────────────────────
 * Expone las tres salidas del solver del motor (v2.5.0 certificado):
 *   • Terreno máximo (max-land)
 *   • Tasa máxima de fondeo (max-rate)
 *   • Funding gap / bache de capital (funding-gap)
 *
 * Reglas: el front solo PRESENTA. No resuelve nada. Los números vienen
 * del motor por los endpoints /api/analyzer/{max-land,max-rate,funding-gap}.
 *
 * Paleta: index.css (Inter, índigo #6366F1, slate). Iconografía Lucide.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Landmark, Percent, Wallet, Loader2, ArrowRight, ShieldCheck, AlertTriangle } from 'lucide-react';
import { analyzerAPI } from '../lib/api';

const fmtMoney = (v) => v == null
  ? '—'
  : `$${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
const fmtPct = (v, d = 2) => v == null ? '—' : `${Number(v).toFixed(d)}%`;

function SolverCard({ icon: Icon, label, value, sub, achieved, hurdle, hurdleLabel, note, loading, errorMsg, testId }) {
  // Veredicto de round-trip: ¿la métrica reinyectada aterriza dentro de ±0.1pt del hurdle?
  const roundtripOk = (achieved != null && hurdle != null)
    ? Math.abs(Number(achieved) - Number(hurdle)) <= 0.1
    : null;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Icon className="h-4 w-4" style={{ color: '#6366F1' }} />
            </div>
            <div>
              <h4 className="text-sm font-display font-semibold leading-tight">{label}</h4>
              {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
            </div>
          </div>
          {roundtripOk === true && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
              <ShieldCheck className="h-3 w-3" /> ID OK
            </span>
          )}
          {roundtripOk === false && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              <AlertTriangle className="h-3 w-3" /> Δ
            </span>
          )}
        </div>

        <div className="mt-3 mb-2">
          {loading ? (
            <div className="flex items-center gap-2 h-9 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> <span className="text-sm">Resolviendo…</span>
            </div>
          ) : errorMsg ? (
            <div className="text-sm text-amber-700 leading-tight">{errorMsg}</div>
          ) : (
            <div className="text-3xl font-display font-semibold slate tabular-nums" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
              {value}
            </div>
          )}
        </div>

        {achieved != null && hurdle != null && !loading && !errorMsg && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground tabular-nums border-t border-border/40 pt-2 mt-3">
            <span>Reinyectado al motor</span>
            <ArrowRight className="h-3 w-3" />
            <span className="font-semibold" style={{ color: '#0F172A' }}>{fmtPct(achieved, 2)}</span>
            <span className="text-muted-foreground">vs {hurdleLabel} {fmtPct(hurdle, 0)}</span>
          </div>
        )}

        {note && !loading && (
          <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">{note}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function SolverPanel({ projectId, scenarioId, analysis }) {
  const [maxLand, setMaxLand] = useState(null);
  const [maxRate, setMaxRate] = useState(null);
  const [fundingGap, setFundingGap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRunDone, setAutoRunDone] = useState(false);

  const runAll = async () => {
    if (!projectId || !scenarioId) return;
    setLoading(true);
    try {
      // Lee el financiamiento REAL del scenario cargado. Si es all-equity
      // (canon Santa Mónica), credit_line = 0 → el gap es el pozo operativo
      // completo. Si el scenario tiene puente, se usa su credit_line y rate
      // reales para el sweep y la nota de autofondeo.
      const a = analysis?.assumptions || {};
      const realCredit = Number(a.credit_line_amount ?? 0);
      const realRate   = Number(a.loan_interest_rate_annual ?? 0);

      const [land, rate, gap] = await Promise.allSettled([
        analyzerAPI.maxLand({ project_id: projectId, scenario_id: scenarioId, stress_absorption_pcts: [], tolerance: 0.1 }),
        analyzerAPI.maxRate({ project_id: projectId, scenario_id: scenarioId, tolerance: 0.1 }),
        analyzerAPI.fundingGap({
          project_id: projectId, scenario_id: scenarioId,
          // ← Financiamiento real del scenario (NO hardcoded). Para canon all-equity = 0.
          credit_line: realCredit,
          loan_rate: realRate,
          // ← Sin estrés: el "funding gap" mostrado es el operativo PURO (definición
          // de Arturo: punto más negativo del flujo acumulado). El sweep estresado
          // de la card "Brecha del socio" queda para una vista futura, no piloto.
          stress_absorption_pct: 0,
          stress_price_pct: 0,
          stress_hard_cost_pct: 0,
        }),
      ]);
      setMaxLand(land.status === 'fulfilled' ? land.value.data : { _err: land.reason?.message || 'error' });
      setMaxRate(rate.status === 'fulfilled' ? rate.value.data : { _err: rate.reason?.message || 'error' });
      setFundingGap(gap.status === 'fulfilled' ? gap.value.data : { _err: gap.reason?.message || 'error' });
    } finally {
      setLoading(false);
      setAutoRunDone(true);
    }
  };

  // Reset when scenario changes
  useEffect(() => {
    setMaxLand(null); setMaxRate(null); setFundingGap(null); setAutoRunDone(false);
  }, [scenarioId, projectId]);

  // Min cumulative cashflow (motor del último run) — para validar funding-gap del solver
  const minCumCash = React.useMemo(() => {
    const sched = analysis?.monthly_schedule || [];
    if (!sched.length) return null;
    const min = sched.reduce((acc, m) => Math.min(acc, Number(m.cumulative_cashflow || 0)), 0);
    return Math.abs(min);
  }, [analysis]);

  // Extract solver readings
  const landValue = maxLand?.base_case?.max_land;
  const landAchieved = maxLand?.base_case?.achieved_irr;
  const landHurdle = maxLand?.hurdle_irr;
  const landStatus = maxLand?.status;
  const landErr = maxLand?._err || (landStatus === 'infeasible' ? maxLand.message : null);

  const rateValue = maxRate?.base_case?.max_rate;
  const rateAchieved = maxRate?.base_case?.achieved_mirr;
  const rateHurdle = maxRate?.hurdle_mirr;
  const rateStatus = maxRate?.status;
  const rateErr = maxRate?._err || (
    rateStatus === 'not_applicable' ? maxRate.message :
    rateStatus === 'unconstrained' ? maxRate.message :
    rateStatus === 'infeasible' ? maxRate.message : null
  );

  const gapValue = fundingGap?.bache_stress_operativo ?? fundingGap?.bache_base_operativo;
  const gapBaseValue = fundingGap?.bache_base_operativo;
  const gapErr = fundingGap?._err;

  return (
    <div className="space-y-4" data-testid="solver-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-display font-semibold" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
            Apalancamiento / Solver
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Salidas del solver inverso del motor. Cada valor se reinyecta al motor y debe aterrizar exactamente en su objetivo.
          </p>
        </div>
        <Button
          onClick={runAll}
          disabled={loading || !projectId || !scenarioId}
          size="sm"
          style={{ backgroundColor: '#6366F1', color: '#fff' }}
          className="hover:brightness-110"
          data-testid="solver-run-btn"
        >
          {loading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Resolviendo</> : (autoRunDone ? 'Recorrer solver' : 'Correr solver')}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SolverCard
          icon={Landmark}
          label="Terreno máximo"
          sub="Pagable al hurdle de MIRR"
          value={landErr ? '—' : fmtMoney(landValue)}
          achieved={landErr ? null : landAchieved}
          hurdle={landHurdle}
          hurdleLabel="hurdle MIRR"
          note="Reinyectando este precio de terreno al motor, la métrica de retorno aterriza exactamente en el hurdle. Pagar arriba de este tope reduce el MIRR por debajo del objetivo."
          loading={loading && !maxLand}
          errorMsg={landErr}
          testId="solver-land-card"
        />
        <SolverCard
          icon={Percent}
          label="Tasa máxima"
          sub="Tasa anual absorbible"
          value={rateErr ? '—' : (rateValue != null ? fmtPct(rateValue, 2) : '—')}
          achieved={rateErr ? null : rateAchieved}
          hurdle={rateHurdle}
          hurdleLabel="hurdle MIRR"
          note="Tasa de crédito más alta que el proyecto absorbe sin que el MIRR caiga por debajo del hurdle. Reinyectando esta tasa, el MIRR aterriza justo en el límite."
          loading={loading && !maxRate}
          errorMsg={rateErr}
          testId="solver-rate-card"
        />
        <SolverCard
          icon={Wallet}
          label="Bache de capital"
          sub="Punto más negativo del flujo"
          value={gapErr ? '—' : fmtMoney(gapValue)}
          achieved={null}
          hurdle={null}
          hurdleLabel=""
          note={
            gapValue != null && minCumCash != null && Math.abs(gapValue - minCumCash) > 100
              ? `Estresado: ${fmtMoney(gapValue)} · Base operativo: ${fmtMoney(gapBaseValue)} · |mín flujo acum.| del run actual: ${fmtMoney(minCumCash)}`
              : 'Magnitud absoluta del peak negativo del flujo acumulado operativo. Es el monto que el equity debe cubrir antes de que entren los ingresos.'
          }
          loading={loading && !fundingGap}
          errorMsg={gapErr}
          testId="solver-gap-card"
        />
      </div>

      {!autoRunDone && !loading && (
        <p className="text-xs text-muted-foreground italic">
          El solver requiere ejecución explícita (toma ~5–15s). Presiona "Correr solver" para resolver las tres salidas.
        </p>
      )}
    </div>
  );
}
